import apiRequest from '@/services/apiRequest';
import { getSearchUrl, TQueryParams } from '@/services/searchApiRequest';
import { IData } from '@/services/types/search/rentals/id';

// Shape of the doc returned by odc-search's `/one-way-listings` (search) and
// `/one-way-listings/:id` (get-by-id) endpoints. Both serve the same indexed
// ES doc, with city/state denormalized off the rental at index time and geo
// points keyed `{lat, lon}` per ES convention. `rental` is only attached by
// the search endpoint (hydrated via mget); the by-id endpoint omits it.
interface IOneWayGeoPoint {
  lat: number;
  lon: number;
}

export interface IOneWayListing {
  id: number;
  rental_id: number;
  owner_user_id: number;
  start_location: IOneWayGeoPoint;
  end_location: IOneWayGeoPoint;
  start_city: string;
  start_state: string;
  start_country: string;
  end_city: string;
  end_state: string;
  end_country: string;
  trip_window_start: string;
  trip_window_end: string;
  included_periods: number;
  extra_period_rate_cents: number;
  distance_meters: number;
  included_miles_per_day: number;
  fuel_reimbursement_cents: number;
  negatives: IOneWayNegative[] | null;
  status: string;
  rental?: IData;
}

interface IOneWayNegative {
  name: string;
  description: string;
}

interface IOneWayListingsResponseEnvelope {
  data: {
    listings: IOneWayListing[];
    meta?: { total: number };
  };
}

export const fetchOneWayListings = async (
  queryParams?: TQueryParams,
): Promise<IOneWayListing[]> => {
  const response = await apiRequest<IOneWayListingsResponseEnvelope>({
    method: 'GET',
    url: getSearchUrl('one-way-listings', queryParams),
  });
  return response?.data?.listings ?? [];
};

// fetchOneWayResultCount calls the search endpoint with `only=meta`, which
// returns the result count without paying for full listing/rental
// hydration. Used by the filter modal preview button.
export const fetchOneWayResultCount = async (queryParams?: TQueryParams): Promise<number> => {
  const response = await apiRequest<IOneWayListingsResponseEnvelope>({
    method: 'GET',
    url: getSearchUrl('one-way-listings', { ...queryParams, only: 'meta' }),
  });
  return response?.data?.meta?.total ?? 0;
};

export interface IOneWayLocation {
  city: string;
  state: string;
}

interface IOneWayLocationsResponseEnvelope {
  data: {
    pickups: IOneWayLocation[];
    dropoffs: IOneWayLocation[];
  };
}

export const fetchOneWayLocationsList = async (): Promise<{
  pickups: IOneWayLocation[];
  dropoffs: IOneWayLocation[];
}> => {
  const response = await apiRequest<IOneWayLocationsResponseEnvelope>({
    method: 'GET',
    url: getSearchUrl('one-way-listings/locations'),
  });
  return {
    pickups: response?.data?.pickups ?? [],
    dropoffs: response?.data?.dropoffs ?? [],
  };
};

interface IOneWayListingByIdResponseEnvelope {
  data: IOneWayListing;
}

export const fetchOneWayListingById = async (id: number | string): Promise<IOneWayListing> => {
  const response = await apiRequest<IOneWayListingByIdResponseEnvelope>({
    method: 'GET',
    url: getSearchUrl(`one-way-listings/${id}`),
  });
  return response.data;
};
